import axios from 'axios';
import {IReactionDisposer, reaction} from 'mobx';
import AppStore from '../../app.store';
import {api} from '../../api/endpoints';
import NetworkStore from '../../network.store';
import {isConnectivityError, isOfflineServiceEnabled} from '../../utils/offline/offline.cache';
import transactionsStore from './transactions.store';
import {
    getOfflineTransactionRecordsAsync,
    OFFLINE_TRANSACTIONS_CHANGED_EVENT,
    OfflineTransactionRecord,
    removeOfflineTransactionFromCache,
    transactionsOfflineInit,
    updateOfflineTransactionSyncState,
} from './transactions.offline';

const SYNC_INTERVAL_MS = 30000;
const SYNC_BATCH_SIZE = 10;
const SYNC_REQUEST_TIMEOUT_MS = 20000;
const LOCK_KEY = 'visor.transactions.offline.sync.lock';
const LOCK_TTL_MS = 45000;

interface OfflineSyncLock {
    owner: string;
    expiresAt: number;
}

const createWorkerId = () => {
    if (typeof globalThis !== 'undefined' && globalThis.crypto?.randomUUID) {
        return globalThis.crypto.randomUUID();
    }

    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

class TransactionsOfflineSyncWorker {
    private started = false;
    private syncing = false;
    private intervalId: number | null = null;
    private timeoutId: number | null = null;
    private networkOnlineDisposer: IReactionDisposer | null = null;
    private readonly workerId = createWorkerId();

    public init = () => {
        if (this.started || typeof window === 'undefined' || !isOfflineServiceEnabled()) {
            return;
        }

        this.started = true;

        window.addEventListener('online', this.handleOnline);
        window.addEventListener('focus', this.handleFocus);
        window.addEventListener(OFFLINE_TRANSACTIONS_CHANGED_EVENT, this.handleQueueChanged);
        document.addEventListener('visibilitychange', this.handleVisibilityChange);

        this.intervalId = window.setInterval(this.scheduleSync, SYNC_INTERVAL_MS);
        this.networkOnlineDisposer = reaction(
            () => NetworkStore.browserOnline && NetworkStore.apiOnline,
            (online) => {
                if (online) {
                    this.scheduleSync(500);
                }
            }
        );
        void transactionsOfflineInit();
        this.scheduleSync(1000);
    };

    public dispose = () => {
        if (typeof window === 'undefined') {
            return;
        }

        window.removeEventListener('online', this.handleOnline);
        window.removeEventListener('focus', this.handleFocus);
        window.removeEventListener(OFFLINE_TRANSACTIONS_CHANGED_EVENT, this.handleQueueChanged);
        document.removeEventListener('visibilitychange', this.handleVisibilityChange);

        if (this.intervalId !== null) {
            window.clearInterval(this.intervalId);
            this.intervalId = null;
        }

        if (this.timeoutId !== null) {
            window.clearTimeout(this.timeoutId);
            this.timeoutId = null;
        }

        this.networkOnlineDisposer?.();
        this.networkOnlineDisposer = null;
        this.releaseLock();
        this.started = false;
    };

    public syncNow = async () => {
        if (this.syncing || !isOfflineServiceEnabled() || !AppStore.getToken()) {
            return;
        }

        if (!this.isBrowserOnline()) {
            return;
        }

        this.syncing = true;

        try {
            const hasPendingRecords = (await getOfflineTransactionRecordsAsync()).length > 0;
            if (!hasPendingRecords || !this.acquireLock()) {
                return;
            }

            const backendOnline = await NetworkStore.checkApiConnection();
            if (!backendOnline) {
                return;
            }

            const records = await getOfflineTransactionRecordsAsync();
            await this.syncRecords(records);
        } finally {
            this.releaseLock();
            this.syncing = false;
        }
    };

    private syncRecords = async (records: OfflineTransactionRecord[]) => {
        const dueRecords = records
            .slice()
            .sort((left, right) => this.getRecordTime(left) - this.getRecordTime(right))
            .slice(0, SYNC_BATCH_SIZE);

        let syncedCount = 0;

        for (const record of dueRecords) {
            if (!this.refreshLock()) {
                break;
            }

            try {
                await this.sendRecord(record);
                await removeOfflineTransactionFromCache(record.id);
                syncedCount += 1;
            } catch (error: any) {
                await this.markFailed(record, error);

                if (isConnectivityError(error) || error?.response?.status === 401) {
                    break;
                }
            }
        }

        if (syncedCount > 0) {
            void transactionsStore.transactionsReq();
        }
    };

    private sendRecord = async (record: OfflineTransactionRecord) => {
        const payload = this.normalizeRequestPayload(record.requestPayload);

        await axios.post(`${api.transactions}?pass_duplicate=true`, payload, {
            headers: AppStore.getHeaders(),
            timeout: SYNC_REQUEST_TIMEOUT_MS,
            __offlineTransactionSync: true,
        } as any);
    };

    private markFailed = async (record: OfflineTransactionRecord, error: any) => {
        const attempts = (record.sync?.attempts ?? 0) + 1;
        const now = new Date();
        const connectivityFailure = isConnectivityError(error) || !error?.response;
        const failureKind = connectivityFailure ? 'connectivity' : 'backend';

        await updateOfflineTransactionSyncState(record.id, {
            attempts,
            lastAttemptAt: now.toISOString(),
            nextRetryAt: now.toISOString(),
            lastError: this.getErrorMessage(error),
            blocked: false,
            failureKind,
            httpStatus: Number(error?.response?.status || 0),
        }, {
            attempt: attempts,
            at: now.toISOString(),
            message: this.getErrorMessage(error),
            failureKind,
            httpStatus: Number(error?.response?.status || 0),
            responseData: error?.response?.data,
        });
    };

    private normalizeRequestPayload = (payload: any) => {
        const products = Array.isArray(payload?.products)
            ? payload.products.filter(Boolean).map((product: any) => ({
                ...product,
                installment_id: this.toFiniteNumber(product.installment_id),
                grant_id: this.toFiniteNumber(product.grant_id),
                variation_id: this.toFiniteNumber(product.variation_id),
            }))
            : [];

        return {
            ...payload,
            pass_duplicate: true,
            products,
        };
    };

    private toFiniteNumber = (value: any) => {
        const numberValue = Number(value);
        return Number.isFinite(numberValue) ? numberValue : 0;
    };

    private getErrorMessage = (error: any) => {
        return (
            error?.response?.data?.reason ||
            error?.response?.data?.error ||
            error?.message ||
            'Не удалось синхронизировать офлайн-транзакцию'
        );
    };

    private getRecordTime = (record: OfflineTransactionRecord) => {
        const createdAt = Date.parse(record.createdAt || '');
        return Number.isNaN(createdAt) ? record.id : createdAt;
    };

    private scheduleSync = (delay = 0) => {
        if (this.timeoutId !== null || typeof window === 'undefined') {
            return;
        }

        this.timeoutId = window.setTimeout(() => {
            this.timeoutId = null;
            void this.syncNow();
        }, delay);
    };

    private handleOnline = () => {
        this.scheduleSync(500);
    };

    private handleFocus = () => {
        this.scheduleSync(500);
    };

    private handleQueueChanged = () => {
        this.scheduleSync(500);
    };

    private handleVisibilityChange = () => {
        if (document.visibilityState === 'visible') {
            this.scheduleSync(500);
        }
    };

    private isBrowserOnline = () => {
        return typeof navigator === 'undefined' || navigator.onLine;
    };

    private acquireLock = () => {
        if (typeof localStorage === 'undefined') {
            return true;
        }

        const now = Date.now();

        try {
            const currentLock = this.readLock();
            if (currentLock && currentLock.owner !== this.workerId && currentLock.expiresAt > now) {
                return false;
            }

            this.writeLock(now + LOCK_TTL_MS);
            return this.readLock()?.owner === this.workerId;
        } catch (error) {
            return true;
        }
    };

    private refreshLock = () => {
        if (typeof localStorage === 'undefined') {
            return true;
        }

        try {
            const currentLock = this.readLock();
            if (currentLock && currentLock.owner !== this.workerId && currentLock.expiresAt > Date.now()) {
                return false;
            }

            this.writeLock(Date.now() + LOCK_TTL_MS);
            return true;
        } catch (error) {
            return true;
        }
    };

    private releaseLock = () => {
        if (typeof localStorage === 'undefined') {
            return;
        }

        try {
            if (this.readLock()?.owner === this.workerId) {
                localStorage.removeItem(LOCK_KEY);
            }
        } catch (error) {
        }
    };

    private readLock = (): OfflineSyncLock | null => {
        const raw = localStorage.getItem(LOCK_KEY);
        if (!raw) {
            return null;
        }

        try {
            return JSON.parse(raw) as OfflineSyncLock;
        } catch (error) {
            return null;
        }
    };

    private writeLock = (expiresAt: number) => {
        localStorage.setItem(LOCK_KEY, JSON.stringify({
            owner: this.workerId,
            expiresAt,
        }));
    };
}

export default new TransactionsOfflineSyncWorker();
